/**
 * Ciel du jeu: degrade, soleil, lune, etoiles et nuages.
 *
 * Particularite importante: la camera du jeu est ORTHOGRAPHIQUE. Tous ses
 * rayons de vue sont paralleles, donc un dome de ciel classique ne montrerait
 * qu'une minuscule tache de sa surface (une seule couleur a l'ecran). Le dome
 * est donc projete avec une camera perspective VIRTUELLE, centree sur la
 * camera du jeu, orientee sur son azimut: on retrouve un vrai ciel peint,
 * avec un horizon, un halo et un soleil ronds, tout en gardant la vue
 * orthographique pour le monde.
 *
 * Le dome, les etoiles et les nuages sont de simples fonds: rendus en premier
 * (renderOrder tres negatif), sans test ni ecriture de profondeur. Le terrain
 * et l'eau peignent par dessus, donc rien n'est jamais masque a tort.
 *
 * Cout: un maillage (32x16), un ShaderMaterial, un THREE.Points de 900 points.
 * Les nuages sont calcules dans le meme shader (deux octaves de bruit), il n'y
 * a donc aucun appel de dessin supplementaire.
 */
import * as THREE from 'three'
import { PHASE_AUBE_DEBUT, PHASE_NUIT_DEBUT } from '../../shared/constants.ts'
import type { Horloge } from '../../shared/sim/temps.ts'
import { hauteurSoleil, joursEcoules } from '../../shared/sim/temps.ts'
import { Alea, bruitValeur, clamp, lerp, melangerCouleur, smoothstep } from '../../shared/sim/alea.ts'

// ------------------------------------------------------------------ reglages

/** Rayon du dome. Purement indicatif: la projection du ciel est virtuelle. */
const RAYON_DOME = 900

/** Rayon des etoiles, juste sous la surface du dome. */
const RAYON_ETOILES = 880

/** Nombre d'etoiles. */
const NB_ETOILES = 900

/** Graine des etoiles: meme ciel a chaque partie. */
const GRAINE_ETOILES = 424242

/**
 * Champ de vision vertical de la camera virtuelle du ciel, en degres.
 * Plus il est grand, plus on voit de ciel au dessus de l'horizon.
 */
const FOV_CIEL = 62

/**
 * Inclinaison de la camera virtuelle du ciel, en degres (positif = vers le
 * haut). Un leger relevement place la ligne d'horizon vers 40 % de la hauteur
 * de l'ecran: le bas est de toute facon couvert par le terrain.
 */
const PITCH_CIEL = 6

/** Elevation maximale du soleil, en fraction de la verticale. */
const APLATISSEMENT_SOLEIL = 0.97

/**
 * Aplatissement de l'arc de la lune. Une lune qui monte au zenith sortirait du
 * champ de la camera pendant toute la nuit: on garde son arc bas sur l'horizon
 * (31 degres au plus), la ou on peut la voir.
 */
const APLATISSEMENT_LUNE = 0.35

const HAUT = new THREE.Vector3(0, 1, 0)

// ------------------------------------------------------------ table de cles

interface CleCiel {
  /** Heure decimale de la cle. */
  readonly h: number
  /** Couleur du ciel au niveau de l'horizon. */
  readonly horizon: number
  /** Couleur du ciel au zenith. */
  readonly zenith: number
  /** Couleur sous la ligne d'horizon (brume lointaine, mer). */
  readonly bas: number
  /** Couleur du halo autour du soleil. */
  readonly halo: number
  /** Force du halo et de la nappe chaude sur l'horizon. */
  readonly forceHalo: number
  /** Couleur du disque solaire. */
  readonly soleil: number
  /** Opacite des etoiles, 0 a 1. */
  readonly etoiles: number
}

/**
 * Couleurs cles du ciel, heure par heure. Interpolees en douceur (smoothstep
 * entre deux cles), en espace sRGB pour rester fidele a la palette choisie.
 * La derniere cle (24 h) reprend celle de minuit pour boucler la journee.
 */
const CLES_CIEL: readonly CleCiel[] = [
  // nuit profonde
  { h: 0.0, horizon: 0x0b1330, zenith: 0x03050f, bas: 0x05070f, halo: 0x2a3a6e, forceHalo: 0.12, soleil: 0xffe9c4, etoiles: 1.0 },
  { h: 4.0, horizon: 0x141c3c, zenith: 0x04070f, bas: 0x060810, halo: 0x33406e, forceHalo: 0.14, soleil: 0xffe9c4, etoiles: 1.0 },
  // la nuit palit, le violet monte
  { h: 5.0, horizon: 0x2b2748, zenith: 0x0a1026, bas: 0x0c1020, halo: 0x6b4a72, forceHalo: 0.35, soleil: 0xffe9c4, etoiles: 0.82 },
  { h: 5.5, horizon: 0x6b4460, zenith: 0x141d40, bas: 0x1a1628, halo: 0xa05a66, forceHalo: 0.6, soleil: 0xffdcae, etoiles: 0.5 },
  { h: 5.85, horizon: 0xb85e58, zenith: 0x243761, bas: 0x33253c, halo: 0xe4804f, forceHalo: 0.95, soleil: 0xffe6bc, etoiles: 0.24 },
  // aube: le soleil touche l'horizon a 6 h
  { h: 6.0, horizon: 0xe0764f, zenith: 0x2f4479, bas: 0x40304a, halo: 0xff9a4e, forceHalo: 1.15, soleil: 0xfff0cc, etoiles: 0.16 },
  { h: 6.75, horizon: 0xf2b183, zenith: 0x3f70b4, bas: 0x8a7a78, halo: 0xffc07a, forceHalo: 0.95, soleil: 0xfff6dd, etoiles: 0.0 },
  // plein jour
  { h: 7.5, horizon: 0xbcd9ee, zenith: 0x3a80c8, bas: 0xa8b8c4, halo: 0xffe3b8, forceHalo: 0.55, soleil: 0xfffaea, etoiles: 0.0 },
  { h: 12.0, horizon: 0xc9e6f7, zenith: 0x2c74c8, bas: 0xb6c8d6, halo: 0xfff6e0, forceHalo: 0.4, soleil: 0xfffdf4, etoiles: 0.0 },
  { h: 17.0, horizon: 0xe0dcc8, zenith: 0x3277c4, bas: 0xbcbfb8, halo: 0xffeec0, forceHalo: 0.5, soleil: 0xfffaea, etoiles: 0.0 },
  // heure doree
  { h: 18.0, horizon: 0xeac093, zenith: 0x396db8, bas: 0x9a8a74, halo: 0xffcf8b, forceHalo: 0.75, soleil: 0xfff4d8, etoiles: 0.0 },
  // soir flamboyant
  { h: 19.0, horizon: 0xf4a35e, zenith: 0x3f63ab, bas: 0x9a8172, halo: 0xffb056, forceHalo: 1.0, soleil: 0xfff0cc, etoiles: 0.0 },
  { h: 20.0, horizon: 0xf07a4a, zenith: 0x4a4a90, bas: 0x6d4f52, halo: 0xff8a3c, forceHalo: 1.35, soleil: 0xffe2b0, etoiles: 0.06 },
  // coucher a 20 h 30
  { h: 20.5, horizon: 0xd8494f, zenith: 0x3a3170, bas: 0x4a2f3c, halo: 0xff6a3a, forceHalo: 1.5, soleil: 0xffcf94, etoiles: 0.22 },
  { h: 21.0, horizon: 0x7e3352, zenith: 0x211d4e, bas: 0x2a1c30, halo: 0xa84a5a, forceHalo: 0.9, soleil: 0xffcf94, etoiles: 0.58 },
  { h: 22.0, horizon: 0x1b1f42, zenith: 0x080c22, bas: 0x0b0e1c, halo: 0x3a3f6e, forceHalo: 0.25, soleil: 0xffe9c4, etoiles: 1.0 },
  { h: 24.0, horizon: 0x0b1330, zenith: 0x03050f, bas: 0x05070f, halo: 0x2a3a6e, forceHalo: 0.12, soleil: 0xffe9c4, etoiles: 1.0 },
]

/**
 * Encadre une heure par deux cles triees et renvoie le facteur d'interpolation
 * deja lisse (smoothstep): la derivee est nulle sur chaque cle, ce qui evite
 * tout saut de couleur perceptible au passage d'une cle a l'autre.
 */
export function encadrerCles<T extends { readonly h: number }>(cles: readonly T[], h: number): { a: T; b: T; t: number } {
  const premier = cles[0] as T
  let a = premier
  let b = cles[cles.length - 1] as T
  for (let i = 0; i + 1 < cles.length; i++) {
    const c = cles[i] as T
    const d = cles[i + 1] as T
    if (h >= c.h && h <= d.h) {
      a = c
      b = d
      break
    }
  }
  const etendue = b.h - a.h
  const brut = etendue > 1e-6 ? clamp((h - a.h) / etendue, 0, 1) : 0
  return { a, b, t: brut * brut * (3 - 2 * brut) }
}

/** Couleurs du ciel interpolees pour une heure donnee. */
export interface EtatCiel {
  /** Couleur au niveau de l'horizon: c'est aussi la couleur de la brume. */
  horizon: number
  zenith: number
  /** Couleur sous la ligne d'horizon. */
  bas: number
  halo: number
  forceHalo: number
  soleil: number
  /** Opacite des etoiles, 0 a 1. */
  etoiles: number
}

/**
 * Etat du ciel pour une heure decimale. Sert au dome, mais aussi a la brume
 * (client/render/lumiere.ts) pour que le brouillard se fonde dans l'horizon.
 */
export function etatCiel(heureDecimale: number): EtatCiel {
  const h = ((heureDecimale % 24) + 24) % 24
  const { a, b, t } = encadrerCles(CLES_CIEL, h)
  return {
    horizon: melangerCouleur(a.horizon, b.horizon, t),
    zenith: melangerCouleur(a.zenith, b.zenith, t),
    bas: melangerCouleur(a.bas, b.bas, t),
    halo: melangerCouleur(a.halo, b.halo, t),
    forceHalo: lerp(a.forceHalo, b.forceHalo, t),
    soleil: melangerCouleur(a.soleil, b.soleil, t),
    etoiles: lerp(a.etoiles, b.etoiles, t),
  }
}

// ------------------------------------------------------------ course du ciel

/**
 * Azimut du soleil en radians: 0 = est (+x), PI/2 = sud (+z), PI = ouest (-x).
 * Le soleil se leve a l'est a PHASE_AUBE_DEBUT, passe au sud, se couche a
 * l'ouest a PHASE_NUIT_DEBUT, puis poursuit sa course sous l'horizon.
 */
function azimutSoleil(heureDecimale: number): number {
  const h = ((heureDecimale % 24) + 24) % 24
  const dureeJour = PHASE_NUIT_DEBUT - PHASE_AUBE_DEBUT
  if (h >= PHASE_AUBE_DEBUT && h <= PHASE_NUIT_DEBUT) {
    return Math.PI * ((h - PHASE_AUBE_DEBUT) / dureeJour)
  }
  const dureeNuit = 24 - dureeJour
  const ecoule = h > PHASE_NUIT_DEBUT ? h - PHASE_NUIT_DEBUT : 24 - PHASE_NUIT_DEBUT + h
  return Math.PI + Math.PI * (ecoule / dureeNuit)
}

/**
 * Direction unitaire pointant vers le soleil (negative en y la nuit).
 * L'elevation vient de hauteurSoleil(); l'azimut d'azimutSoleil().
 */
export function directionSoleil(heureDecimale: number, sortie: THREE.Vector3): THREE.Vector3 {
  const elevation = Math.asin(clamp(hauteurSoleil(heureDecimale), -1, 1) * APLATISSEMENT_SOLEIL)
  const azimut = azimutSoleil(heureDecimale)
  const cosE = Math.cos(elevation)
  return sortie.set(Math.cos(azimut) * cosE, Math.sin(elevation), Math.sin(azimut) * cosE)
}

/**
 * Direction unitaire pointant vers la lune. Elle est a l'oppose du soleil en
 * azimut (elle se leve donc a l'est quand le soleil se couche a l'ouest) mais
 * son arc est aplati pour rester visible. Negative en y le jour.
 */
export function directionLune(heureDecimale: number, sortie: THREE.Vector3): THREE.Vector3 {
  const elevation = Math.asin(clamp(-hauteurSoleil(heureDecimale), -1, 1)) * APLATISSEMENT_LUNE
  const azimut = azimutSoleil(heureDecimale) + Math.PI
  const cosE = Math.cos(elevation)
  return sortie.set(Math.cos(azimut) * cosE, Math.sin(elevation), Math.sin(azimut) * cosE)
}

/**
 * Couverture nuageuse du moment, 0 a 1. Fonction lente et continue du temps
 * de jeu: certains jours sont degages, d'autres bien charges.
 */
function couvertureNuages(tempsJeu: number): number {
  return 0.16 + 0.46 * bruitValeur(joursEcoules(tempsJeu), 0.37, 9176)
}

// ------------------------------------------------------------------- shaders

const VS_DOME = /* glsl */ `
uniform mat4 uVueProjCiel;
varying vec3 vDir;
void main() {
  vDir = position;
  gl_Position = uVueProjCiel * modelMatrix * vec4(position, 1.0);
}
`

const FS_DOME = /* glsl */ `
uniform vec3 uHorizon;
uniform vec3 uZenith;
uniform vec3 uBas;
uniform vec3 uHalo;
uniform vec3 uCouleurSoleil;
uniform vec3 uCouleurLune;
uniform vec3 uNuageClair;
uniform vec3 uNuageOmbre;
uniform vec3 uDirSoleil;
uniform vec3 uDirLune;
uniform vec3 uCentreCroissant;
uniform float uForceHalo;
uniform float uOpaciteSoleil;
uniform float uOpaciteLune;
uniform float uCouverture;
uniform float uTemps;
varying vec3 vDir;

// Hachage sans sinus: moins cher qu'un sin et surtout stable quand les
// coordonnees grandissent (la derive des nuages s'accumule avec le temps).
float hacheCiel(vec2 p) {
  vec3 q = fract(vec3(p.x, p.y, p.x) * vec3(0.1031, 0.1030, 0.0973));
  q += dot(q, vec3(q.y, q.z, q.x) + 33.33);
  return fract((q.x + q.y) * q.z);
}

float bruitCiel(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hacheCiel(i);
  float b = hacheCiel(i + vec2(1.0, 0.0));
  float c = hacheCiel(i + vec2(0.0, 1.0));
  float d = hacheCiel(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

void main() {
  vec3 d = normalize(vDir);
  float hauteur = d.y;

  // Degrade vertical, plus serre pres de l'horizon (0.45 imite l'atmosphere).
  float t = pow(clamp(hauteur, 0.0, 1.0), 0.45);
  vec3 col = mix(uHorizon, uZenith, t);
  col = mix(uBas, col, smoothstep(-0.07, 0.015, hauteur));

  // Nappe chaude etalee le long de l'horizon, du cote du soleil.
  vec2 azD = vec2(d.x, d.z);
  vec2 azS = vec2(uDirSoleil.x, uDirSoleil.z);
  float norme = length(azD) * length(azS);
  float cote = norme > 1e-4 ? max(dot(azD, azS) / norme, 0.0) : 0.0;
  float bandeau = pow(cote, 3.0) * (1.0 - smoothstep(-0.10, 0.55, hauteur));

  // Halo serre autour du soleil.
  float ds = max(dot(d, uDirSoleil), 0.0);
  float halo = pow(ds, 7.0) * 0.35 + pow(ds, 90.0) * 0.9;
  col += uHalo * (halo + bandeau * 0.45) * uForceHalo;

  // Nuages: deux octaves sur un plan projete, derive lente.
  if (uCouverture > 0.01 && hauteur > 0.02) {
    vec2 p = vec2(d.x, d.z) / max(hauteur, 0.10) * 0.55 + vec2(uTemps * 0.0045, uTemps * 0.0022);
    float n = bruitCiel(p) * 0.65 + bruitCiel(p * 2.7 + 11.3) * 0.35;
    float masque = smoothstep(0.02, 0.28, hauteur);
    float couvre = smoothstep(0.50, 0.80, n) * uCouverture * masque;
    float eclat = clamp(smoothstep(0.45, 0.85, n) * 0.7 + ds * 0.5, 0.0, 1.0);
    col = mix(col, mix(uNuageOmbre, uNuageClair, eclat), couvre);
  }

  // Disque solaire.
  float distSoleil = length(d - uDirSoleil);
  col += uCouleurSoleil * (1.0 - smoothstep(0.028, 0.036, distSoleil)) * uOpaciteSoleil;

  // Croissant de lune: un disque moins un disque decale.
  float pleine = 1.0 - smoothstep(0.024, 0.030, length(d - uDirLune));
  float entaille = 1.0 - smoothstep(0.022, 0.029, length(d - uCentreCroissant));
  float croissant = clamp(pleine - entaille, 0.0, 1.0);
  float dl = max(dot(d, uDirLune), 0.0);
  col += uCouleurLune * (croissant * 1.1 + pow(dl, 200.0) * 0.35) * uOpaciteLune;

  gl_FragColor = vec4(col, 1.0);
  #include <colorspace_fragment>
}
`

const VS_ETOILES = /* glsl */ `
uniform mat4 uVueProjCiel;
uniform float uTemps;
uniform float uOpacite;
uniform float uEchelle;
attribute float aTaille;
attribute float aPhase;
attribute vec3 aTeinte;
varying vec3 vTeinte;
varying float vAlpha;
void main() {
  vTeinte = aTeinte;
  // Scintillement tres leger: taille et eclat respirent en dephasage.
  float respire = 0.78 + 0.30 * sin(uTemps * 2.3 + aPhase);
  vAlpha = uOpacite * (0.62 + 0.38 * sin(uTemps * 1.7 + aPhase * 1.7));
  gl_Position = uVueProjCiel * modelMatrix * vec4(position, 1.0);
  // Jamais sous 1 pixel: certains pilotes n'affichent rien en dessous.
  gl_PointSize = max(aTaille * uEchelle * respire, 1.0);
}
`

const FS_ETOILES = /* glsl */ `
varying vec3 vTeinte;
varying float vAlpha;
void main() {
  float r = length(gl_PointCoord - vec2(0.5));
  float a = (1.0 - smoothstep(0.12, 0.5, r)) * vAlpha;
  if (a <= 0.003) discard;
  // Couleur non premultipliee: AdditiveBlending applique deja SRC_ALPHA.
  gl_FragColor = vec4(vTeinte, a);
  #include <colorspace_fragment>
}
`

// --------------------------------------------------------------------- classe

/** Teintes possibles des etoiles: blanc, bleute, ambre. */
const TEINTES_ETOILES: readonly number[] = [0xffffff, 0xdce8ff, 0xc8d6ff, 0xfff0d8, 0xffe0b4]

export class Ciel {
  /** Groupe qui porte le dome et les etoiles. Suit la camera. */
  readonly groupe: THREE.Group

  private readonly scene: THREE.Scene
  private readonly dome: THREE.Mesh
  private readonly matDome: THREE.ShaderMaterial
  private readonly etoiles: THREE.Points
  private readonly matEtoiles: THREE.ShaderMaterial

  /** Camera perspective virtuelle: seule sa matrice sert (voir en tete). */
  private readonly camVirtuelle: THREE.PerspectiveCamera
  private readonly vueProj = new THREE.Matrix4()
  private aspect = 1

  private readonly dirSoleil = new THREE.Vector3(1, 0, 0)
  private readonly dirLune = new THREE.Vector3(-1, 0, 0)
  private readonly centreCroissant = new THREE.Vector3()
  private readonly cibleRegard = new THREE.Vector3()
  private readonly droiteCiel = new THREE.Vector3()

  // Couleurs et scalaires partages avec les shaders: on garde la reference et
  // on la mute, plutot que d'aller la rechercher dans material.uniforms.
  private readonly cHorizon = new THREE.Color(0x0b1330)
  private readonly cZenith = new THREE.Color(0x03050f)
  private readonly cBas = new THREE.Color(0x05070f)
  private readonly cHalo = new THREE.Color(0x2a3a6e)
  private readonly cSoleil = new THREE.Color(0xffe9c4)
  private readonly cLune = new THREE.Color(0xdce8ff)
  private readonly cNuageClair = new THREE.Color(0xffffff)
  private readonly cNuageOmbre = new THREE.Color(0x445566)
  private readonly uForceHalo = { value: 0.5 }
  private readonly uOpaciteSoleil = { value: 0 }
  private readonly uOpaciteLune = { value: 0 }
  private readonly uCouverture = { value: 0.35 }
  private readonly uTemps = { value: 0 }
  private readonly uOpaciteEtoiles = { value: 0 }

  constructor(scene: THREE.Scene) {
    this.scene = scene
    // Le dome remplace le fond de scene: inutile de peindre deux fois l'ecran.
    scene.background = null

    this.groupe = new THREE.Group()
    this.groupe.name = 'ciel'
    this.groupe.frustumCulled = false

    this.camVirtuelle = new THREE.PerspectiveCamera(FOV_CIEL, 1, 1, RAYON_DOME * 2)

    this.matDome = new THREE.ShaderMaterial({
      uniforms: {
        uVueProjCiel: { value: this.vueProj },
        uHorizon: { value: this.cHorizon },
        uZenith: { value: this.cZenith },
        uBas: { value: this.cBas },
        uHalo: { value: this.cHalo },
        uCouleurSoleil: { value: this.cSoleil },
        uCouleurLune: { value: this.cLune },
        uNuageClair: { value: this.cNuageClair },
        uNuageOmbre: { value: this.cNuageOmbre },
        uDirSoleil: { value: this.dirSoleil },
        uDirLune: { value: this.dirLune },
        uCentreCroissant: { value: this.centreCroissant },
        uForceHalo: this.uForceHalo,
        uOpaciteSoleil: this.uOpaciteSoleil,
        uOpaciteLune: this.uOpaciteLune,
        uCouverture: this.uCouverture,
        uTemps: this.uTemps,
      },
      vertexShader: VS_DOME,
      fragmentShader: FS_DOME,
      side: THREE.BackSide,
      depthTest: false,
      depthWrite: false,
      fog: false,
    })

    this.dome = new THREE.Mesh(new THREE.SphereGeometry(RAYON_DOME, 32, 16), this.matDome)
    this.dome.name = 'ciel-dome'
    this.dome.frustumCulled = false
    // Fond: dessine avant tout le reste, sans profondeur.
    this.dome.renderOrder = -1000
    this.groupe.add(this.dome)

    this.matEtoiles = new THREE.ShaderMaterial({
      uniforms: {
        uVueProjCiel: { value: this.vueProj },
        uTemps: this.uTemps,
        uOpacite: this.uOpaciteEtoiles,
        uEchelle: { value: Math.min(window.devicePixelRatio || 1, 2) },
      },
      vertexShader: VS_ETOILES,
      fragmentShader: FS_ETOILES,
      blending: THREE.AdditiveBlending,
      // transparent reste faux: les etoiles doivent rester dans la passe
      // opaque, juste apres le dome, avant que le monde ne peigne par dessus.
      transparent: false,
      depthTest: false,
      depthWrite: false,
      fog: false,
    })

    this.etoiles = new THREE.Points(this.creerGeometrieEtoiles(), this.matEtoiles)
    this.etoiles.name = 'ciel-etoiles'
    this.etoiles.frustumCulled = false
    this.etoiles.renderOrder = -999
    this.groupe.add(this.etoiles)

    scene.add(this.groupe)
  }

  /** Etoiles reparties uniformement sur l'hemisphere superieur. */
  private creerGeometrieEtoiles(): THREE.BufferGeometry {
    const alea = new Alea(GRAINE_ETOILES)
    const positions = new Float32Array(NB_ETOILES * 3)
    const tailles = new Float32Array(NB_ETOILES)
    const phases = new Float32Array(NB_ETOILES)
    const teintes = new Float32Array(NB_ETOILES * 3)
    const couleur = new THREE.Color()

    for (let i = 0; i < NB_ETOILES; i++) {
      const azimut = alea.entre(0, Math.PI * 2)
      // y uniforme = repartition uniforme sur la surface de l'hemisphere.
      const y = alea.entre(0.05, 1)
      const r = Math.sqrt(Math.max(0, 1 - y * y))
      positions[i * 3] = Math.cos(azimut) * r * RAYON_ETOILES
      positions[i * 3 + 1] = y * RAYON_ETOILES
      positions[i * 3 + 2] = Math.sin(azimut) * r * RAYON_ETOILES

      // Quelques etoiles nettement plus brillantes que les autres.
      tailles[i] = alea.chance(0.08) ? alea.entre(2.4, 3.6) : alea.entre(1.0, 2.0)
      phases[i] = alea.entre(0, Math.PI * 2)
      couleur.setHex(alea.choix(TEINTES_ETOILES))
      teintes[i * 3] = couleur.r
      teintes[i * 3 + 1] = couleur.g
      teintes[i * 3 + 2] = couleur.b
    }

    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aTaille', new THREE.BufferAttribute(tailles, 1))
    geo.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1))
    geo.setAttribute('aTeinte', new THREE.BufferAttribute(teintes, 3))
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), RAYON_ETOILES)
    return geo
  }

  /** Recentre le ciel sur la camera. Appele aussi par maj() si besoin. */
  suivre(positionCamera: THREE.Vector3): void {
    this.groupe.position.copy(positionCamera)
    this.camVirtuelle.position.copy(positionCamera)
  }

  /**
   * Met le ciel a l'heure. `azimutCamera` est l'azimut de la camera du jeu
   * (celui de shared/constants.ts CAM_AZIMUTH par defaut): le ciel tourne avec
   * elle, sinon le soleil ne serait pas au bon endroit a l'ecran.
   *
   * L'aspect est lu sur la fenetre: cela suppose que la toile occupe toute la
   * fenetre, comme le fait client/main.ts. Si un jour la toile est plus petite,
   * c'est ici qu'il faudra prendre sa taille reelle.
   */
  maj(h: Horloge, azimutCamera: number): void {
    const hd = h.heureDecimale
    const etat = etatCiel(hd)

    // --- matrice du ciel: camera perspective virtuelle sur l'azimut du jeu.
    const largeur = window.innerWidth
    const hauteurEcran = window.innerHeight
    const aspect = hauteurEcran > 0 ? largeur / hauteurEcran : 1
    if (Math.abs(aspect - this.aspect) > 1e-3) {
      this.aspect = aspect
      this.camVirtuelle.aspect = aspect
      this.camVirtuelle.updateProjectionMatrix()
    }
    // La camera du jeu est placee a l'azimut donne et regarde vers sa cible,
    // donc sa direction de vue horizontale est l'oppose de l'azimut.
    const pente = Math.tan((PITCH_CIEL * Math.PI) / 180)
    this.cibleRegard.set(-Math.cos(azimutCamera), pente, -Math.sin(azimutCamera)).add(this.camVirtuelle.position)
    this.camVirtuelle.lookAt(this.cibleRegard)
    this.camVirtuelle.updateMatrixWorld(true)
    this.vueProj.multiplyMatrices(this.camVirtuelle.projectionMatrix, this.camVirtuelle.matrixWorldInverse)

    // --- astres
    directionSoleil(hd, this.dirSoleil)
    directionLune(hd, this.dirLune)
    // Entaille du croissant, decalee vers la droite et vers le haut.
    this.droiteCiel.crossVectors(this.dirLune, HAUT)
    if (this.droiteCiel.lengthSq() < 1e-6) this.droiteCiel.set(1, 0, 0)
    else this.droiteCiel.normalize()
    this.centreCroissant
      .copy(this.dirLune)
      .addScaledVector(this.droiteCiel, 0.013)
      .addScaledVector(HAUT, 0.007)

    // --- couleurs
    this.cHorizon.setHex(etat.horizon)
    this.cZenith.setHex(etat.zenith)
    this.cBas.setHex(etat.bas)
    this.cHalo.setHex(etat.halo)
    this.cSoleil.setHex(etat.soleil)
    // Les nuages se peignent tout seuls avec la palette du moment.
    this.cNuageClair.setHex(melangerCouleur(melangerCouleur(etat.horizon, etat.halo, 0.45), 0xffffff, 0.25))
    this.cNuageOmbre.setHex(melangerCouleur(melangerCouleur(etat.zenith, etat.horizon, 0.35), 0x000000, 0.15))

    this.uForceHalo.value = etat.forceHalo
    this.uOpaciteSoleil.value = smoothstep(-0.02, 0.05, this.dirSoleil.y)
    this.uOpaciteLune.value = smoothstep(-0.01, 0.07, this.dirLune.y)
    this.uCouverture.value = couvertureNuages(h.tempsJeu)
    // Borne le temps pour garder de la precision en float32 dans les shaders.
    this.uTemps.value = (h.tempsJeu / 1000) % 100000

    // --- etoiles
    this.uOpaciteEtoiles.value = etat.etoiles
    this.etoiles.visible = etat.etoiles > 0.01
  }

  dispose(): void {
    this.scene.remove(this.groupe)
    this.dome.geometry.dispose()
    this.matDome.dispose()
    this.etoiles.geometry.dispose()
    this.matEtoiles.dispose()
  }
}
